import featureNames from './featureNames';

const dashboard = {
  en: {
    startWorkout: 'Start Workout',
    homeEmpty: {
      title: 'Your next chapter starts with a note',
      message: 'Choose a course in the library or add your own sentences to start training.',
    },
    trainingHero: {
      title: "Today's training",
      completedPrompt: 'Great work today. Ready for another step?',
      unavailable: 'Your streak is temporarily unavailable.',
      streakLabel: 'Current streak',
      todayPoints: "Today's training",
      starting: 'Preparing workout…',
    },
    homePreview: {
      label: 'Remember these expressions?',
      sectionLabel: 'More ways to train',
      autoAdvance: 'Auto-advance',
      writingOpen: 'Open writing practice',
      writingError: 'We couldn’t load the writing preview. Please try again.',
      libraryError: 'We couldn’t load recommended notes. Please try again.',
      pause: 'Pause previews',
      resume: 'Resume previews',
      loading: 'Loading your expressions…',
      error: 'We couldn’t load your previews. You can still open your cards.',
      retry: 'Retry previews',
      levelUpEmpty: 'Train cards to their upgrade target.',
      triumphEmpty: 'Complete a card to unlock review.',
      libraryEmpty: 'Published Market notes will appear here.',
      noPreview: 'Open your cards to continue.',
      libraryNoPreview: 'Explore notes in the Library.',
      question: 'Question',
      answer: 'Answer',
      showQuestion: 'Show question',
      showAnswer: 'Show answer',
      levelUpOpen: 'Open eligible card upgrades',
      triumphOpen: 'Review all completed cards',
      libraryOpen: 'Open this Library note',
    },
    practicePool: {
      level1: 'Keep your practice growing. Add a new note or more cards.',
      level2: 'Your practice pool is running low. Add a new note or more cards soon.',
      level3: 'No cards left to practice. Add a new note or cards, or enable a note to get started.',
    },
    library: 'Library',
    browseLibrary: 'Browse preset library',
    addNote: 'Add New Note',
    addNoteLabel: 'Add new note',
    newNoteShort: 'New note',
    noteActions: 'Manage your notes',
    levelUpTitle: featureNames.en.levelUp,
    levelUpOpen: 'Open eligible card upgrades',
    levelUpLocked: 'Train cards to their upgrade target.',
    levelUpReady: '{count} ready',
    triumphTitle: featureNames.en.triumph,
    triumphOpen: 'Review all completed cards',
    triumphLocked: 'Complete a card to unlock review.',
    triumphReady: '{count} completed',
    libraryTitle: 'Library',
    libraryReady: '{count} notes total',
    libraryLocked: 'No published notes yet.',
    errors: {
      signIn: 'Please log in to continue.',
      loadNotes: 'Failed to load notes from database',
      startWorkout: 'Failed to start workout',
      prepareWorkout: 'Failed to prepare workout',
      startPractice: 'Failed to start practice',
      openImport: 'Failed to open import interface',
      openCards: 'Failed to open flashcard list',
      startPlayer: 'Failed to start Practice Player',
      openCalendar: 'Failed to open calendar',
      openTournament: 'Failed to open tournament',
      openLevelUp: 'Failed to open level-up page',
      openTriumph: 'Failed to open Sentence Archive',
      openLibrary: 'Failed to open Library',
    },
    tournament: {
      title: featureNames.en.tournament,
      open: 'Open the world martial arts tournament',
      signupOpen: 'Signup Open',
      matchDay: 'Match Day',
      nextArena: 'Next Arena',
      board: 'Arena Board',
      day: 'Day',
      entrants: 'In',
      maximum: 'Max',
      loading: 'Loading arena schedule...',
      signedUp: 'Signed up - {count}/32 joined',
      signupCloses: 'Signup closes {date}',
      todayMatch: 'Today: {home} vs {away}',
      fighter: 'Fighter',
      inProgress: '{phase} in progress',
      cancelled: 'This month was cancelled. Next arena is coming soon.',
      lastResult: 'Last result: #{rank} {result}',
      next: 'Next: {date}. Signup opens {signupDate}',
      labels: {
        groupStage: 'Group Stage', roundOf16: 'Round of 16',
        quarterFinal: 'Quarter-final', semiFinal: 'Semi-final', final: 'Final',
        champion: 'Champion', runnerUp: 'Runner-up',
        semiFinalist: 'Semi-finalist', quarterFinalist: 'Quarter-finalist',
      },
    },
    streak: {
      title: 'Training Log',
      open: 'Open training log',
      view: 'View training log',
      loading: 'Loading...',
      day: '{count} day',
      days: '{count} days',
      trained: 'Trained today',
      notTrained: 'Not trained today',
      label: 'Current streak: {count} days. {status}. Click to view training log.',
      trainer: '{name} {status} calendar trainer',
      cheerful: 'cheerful',
      depressed: 'depressed',
      normal: 'normal',
      active0: 'Your next rep starts now.',
      active1: 'Keep the streak alive.',
      active2: 'One more rep. Keep it going.',
      active3: 'You’re on a roll. Don’t stop now.',
      fresh0: 'Start today. Build your streak.',
      fresh1: 'Day one starts now.',
      fresh2: 'Start fresh. Build it back.',
      fresh3: 'New streak. Same goal.',
    },
    notes: {
      title: 'My Drills',
      pageSubtitle: 'Open a note, manage your cards, or train one collection at a time.',
      loading: 'Loading drills...',
      emptyTitle: 'No Drills Yet',
      emptyMessage: 'Get started by importing your first drill or creating flashcards.',
      countOne: '{count} drill',
      countMany: '{count} drills',
      completedCount: 'Completed notes: {count}',
      hideCompleted: 'Hide completed notes',
      showCompleted: 'Show completed notes',
      completedHidden: 'All notes are complete. Turn on completed notes to show them.',
      reorder: 'Drag to reorder',
      unknownDate: 'Unknown date',
      open: 'Open note: {title}, {count} flashcards, {state}',
      cover: 'Cover for {title}',
      new: 'NEW',
      completed: 'Completed',
      allCompleted: 'All {count} cards completed',
      someCompleted: '{completed} of {count} cards completed',
      options: 'Options',
      openOptions: 'Open options for {title}',
      optionsFor: 'Options for {title}',
      practice: 'Practice',
      edit: 'Edit',
      move: 'Move',
    },
  },
  ko: {
    startWorkout: '훈련 시작',
    homeEmpty: {
      title: '학습할 노트부터 준비해 볼까요?',
      message: '도서관에서 코스를 가져오거나 직접 문장을 추가해 훈련을 시작하세요.',
    },
    trainingHero: {
      title: '오늘의 훈련',
      completedPrompt: '오늘도 잘 해냈어요. 원하면 조금 더 함께해요!',
      unavailable: '지금은 연속 학습 기록을 확인할 수 없어요.',
      streakLabel: '연속 학습',
      todayPoints: '오늘 훈련',
      starting: '훈련 준비 중…',
    },
    homePreview: {
      label: '잠깐, 이 문장 기억나세요?',
      sectionLabel: '다양한 학습과 복습',
      autoAdvance: '자동 넘김',
      writingOpen: '영작 훈련 열기',
      writingError: '영작 미리보기를 불러오지 못했어요. 다시 시도해 주세요.',
      libraryError: '추천 노트를 불러오지 못했어요. 다시 시도해 주세요.',
      pause: '자동 넘김 멈추기',
      resume: '자동 넘김 시작',
      loading: '학습 문장을 불러오는 중…',
      error: '문장 미리보기를 불러오지 못했어요. 카드 화면은 열 수 있어요.',
      retry: '미리보기 다시 불러오기',
      levelUpEmpty: '카드의 레벨업 목표를 달성하면 열려요.',
      triumphEmpty: '카드를 완료하면 복습할 수 있어요.',
      libraryEmpty: '마켓에 공개된 표현이 생기면 보여 드릴게요.',
      noPreview: '카드 화면에서 학습을 이어 가세요.',
      libraryNoPreview: '도서관에서 노트를 둘러보세요.',
      question: '질문',
      answer: '답변',
      showQuestion: '질문 보기',
      showAnswer: '답변 보기',
      levelUpOpen: '레벨업 가능한 카드 보기',
      triumphOpen: '완료한 카드 복습하기',
      libraryOpen: '이 도서관 노트 열기',
    },
    practicePool: {
      level1: '꾸준히 연습할 수 있도록 새 노트나 카드를 추가해 보세요.',
      level2: '연습할 카드가 얼마 남지 않았어요. 새 노트나 카드를 추가해 주세요.',
      level3: '연습할 카드가 없어요. 새 노트나 카드를 추가하거나 노트를 활성화해 시작해 보세요.',
    },
    library: '도서관',
    browseLibrary: '도서관 둘러보기',
    addNote: '새 노트 추가',
    addNoteLabel: '새 노트 추가',
    newNoteShort: '새노트',
    noteActions: '학습 노트 관리',
    levelUpTitle: featureNames.ko.levelUp,
    levelUpOpen: '레벨업 가능한 카드 보기',
    levelUpLocked: '카드의 레벨업 목표를 달성하면 열려요.',
    levelUpReady: '도전 가능한 카드 {count}장',
    triumphTitle: featureNames.ko.triumph,
    triumphOpen: '완료한 카드 복습하기',
    triumphLocked: '카드를 완료하면 복습할 수 있어요.',
    triumphReady: '학습 완료한 카드 {count}장',
    libraryTitle: '도서관',
    libraryReady: '총 노트 {count}개',
    libraryLocked: '아직 공개된 노트가 없어요.',
    errors: {
      signIn: '계속하려면 로그인해 주세요.',
      loadNotes: '학습 노트를 불러오지 못했어요.',
      startWorkout: '훈련을 시작하지 못했어요.',
      prepareWorkout: '훈련을 준비하지 못했어요.',
      startPractice: '연습을 시작하지 못했어요.',
      openImport: '노트 추가 화면을 열지 못했어요.',
      openCards: '카드 목록을 열지 못했어요.',
      startPlayer: '듣기 훈련을 시작하지 못했어요.',
      openCalendar: '훈련 기록을 열지 못했어요.',
      openTournament: '대회 화면을 열지 못했어요.',
      openLevelUp: '카드 레벨업 화면을 열지 못했어요.',
      openTriumph: '문장 보관함을 열지 못했어요.',
      openLibrary: '도서관을 열지 못했어요.',
    },
    tournament: {
      title: featureNames.ko.tournament,
      open: '세계 대회 참가 및 기록 보기',
      signupOpen: '참가 신청 중',
      matchDay: '경기일',
      nextArena: '다음 대회',
      board: '대회 현황',
      day: '일차',
      entrants: '참가',
      maximum: '정원',
      loading: '대회 일정을 불러오는 중…',
      signedUp: '신청 완료 · {count}/32명 참가',
      signupCloses: '{date} 신청 마감',
      todayMatch: '오늘 경기: {home} 대 {away}',
      fighter: '선수',
      inProgress: '{phase} 진행 중',
      cancelled: '이번 달 대회가 취소되었어요. 다음 대회를 기다려 주세요.',
      lastResult: '지난 결과: {rank}위 {result}',
      next: '다음 대회 {date} · {signupDate} 신청 시작',
      labels: {
        groupStage: '조별 리그', roundOf16: '16강',
        quarterFinal: '8강', semiFinal: '준결승', final: '결승',
        champion: '우승', runnerUp: '준우승',
        semiFinalist: '4강 진출', quarterFinalist: '8강 진출',
      },
    },
    streak: {
      title: '훈련 기록',
      open: '훈련 기록 보기',
      view: '훈련 기록 보기',
      loading: '불러오는 중…',
      day: '{count}일',
      days: '{count}일',
      trained: '오늘 훈련 완료',
      notTrained: '오늘 훈련 전',
      label: '연속 훈련 {count}일. {status}. 훈련 기록 보기.',
      trainer: '{name} 트레이너 ({status})',
      cheerful: '응원 중',
      depressed: '훈련을 기다리는 중',
      normal: '준비 완료',
      active0: '다음 훈련, 지금 시작해 볼까요?',
      active1: '꾸준한 훈련을 이어 가요.',
      active2: '한 번 더! 계속 나아가요.',
      active3: '잘하고 있어요. 오늘도 함께해요!',
      fresh0: '오늘부터 차근차근 시작해요.',
      fresh1: '오늘이 새로운 첫날이에요.',
      fresh2: '다시 시작해도 괜찮아요.',
      fresh3: '새로운 시작, 같은 목표!',
    },
    notes: {
      title: '내 노트',
      pageSubtitle: '노트를 열어 카드를 관리하거나 원하는 노트만 골라 학습하세요.',
      loading: '학습 노트를 불러오는 중…',
      emptyTitle: '아직 학습 노트가 없어요',
      emptyMessage: '도서관에서 노트를 가져오거나 새 노트를 추가해 보세요.',
      countOne: '노트 {count}개',
      countMany: '노트 {count}개',
      completedCount: '완료 노트 {count}개',
      hideCompleted: '완료 노트 숨기기',
      showCompleted: '완료 노트 표시하기',
      completedHidden: '모든 노트가 완료되었어요. 완료 노트 표시를 켜면 다시 볼 수 있어요.',
      reorder: '드래그하여 순서 변경',
      unknownDate: '날짜 정보 없음',
      open: '노트 열기: {title}, 카드 {count}개, {state}',
      cover: '{title} 표지',
      new: '새 노트',
      completed: '완료',
      allCompleted: '카드 {count}개 모두 완료',
      someCompleted: '카드 {count}개 중 {completed}개 완료',
      options: '노트 메뉴',
      openOptions: '{title} 노트 메뉴 열기',
      optionsFor: '{title} 노트 메뉴',
      practice: '훈련하기',
      edit: '수정하기',
      move: '순서 변경',
    },
  },
};

export default dashboard;
